/**
 * What the integration contract contributes to the route tree.
 *
 * Each route declares the permission it needs, and the same declaration drives
 * both the router's gate and the navigation entry — so a menu item cannot
 * appear for a page the user would be refused.
 *
 * The split follows the backend's, by consequence: reading the journal,
 * editing the mapping, and pointing the installation at an external system are
 * three different jobs. The ADV maps fields (§3 of the cadrage); the owner
 * configures the connector and issues machine credentials.
 *
 * `/integration/mapping/$entity` carries no `nav` entry: it is reached from
 * the entity list or from the synchronisation table, not from a menu.
 */

import { useParams } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import type { FeatureRoutes } from "@hardauto/core-router";

import { ConnectorScreen } from "./screens/ConnectorScreen";
import { MappingIndexScreen, MappingScreen } from "./screens/MappingScreen";
import { SyncScreen } from "./screens/SyncScreen";
import { INTEGRATION_PATHS } from "./paths";
import "./i18n";

export function integrationRoutes(client: ApiClient): FeatureRoutes {
  function Sync() {
    return <SyncScreen client={client} />;
  }

  function Connector() {
    return <ConnectorScreen client={client} />;
  }

  function MappingIndex() {
    return <MappingIndexScreen client={client} />;
  }

  function Mapping() {
    // `strict: false` because the route tree is assembled at runtime from a
    // registry, so TanStack cannot infer per-route parameter types. The entity
    // code is validated by the server on every call it is used in.
    const params: { entity?: string } = useParams({ strict: false });
    return <MappingScreen client={client} entity={params.entity ?? ""} />;
  }

  return {
    id: "integration",
    routes: [
      {
        path: INTEGRATION_PATHS.sync,
        component: Sync,
        layout: "app",
        requirement: { authenticated: true, permissions: ["integration.journal.read"] },
        nav: {
          group: "integration:group",
          labelKey: "integration:nav.sync",
          section: "main",
          order: 70,
        },
      },
      {
        path: INTEGRATION_PATHS.mapping,
        component: MappingIndex,
        layout: "app",
        requirement: { authenticated: true, permissions: ["integration.mapping.read"] },
        nav: {
          group: "integration:group",
          labelKey: "integration:nav.mapping",
          section: "main",
          order: 71,
        },
      },
      {
        path: INTEGRATION_PATHS.entityMapping,
        component: Mapping,
        layout: "app",
        requirement: { authenticated: true, permissions: ["integration.mapping.read"] },
      },
      {
        path: INTEGRATION_PATHS.connector,
        component: Connector,
        layout: "app",
        // `connector.read` and not `connector.write`: an ADV may look at which
        // system the installation talks to. The screen renders every control
        // from `connector.write` and `credential.write`, so reading it grants
        // nothing.
        requirement: { authenticated: true, permissions: ["integration.connector.read"] },
        nav: {
          group: "integration:group",
          labelKey: "integration:nav.connector",
          section: "main",
          order: 72,
        },
      },
    ],
  };
}
